import React, { useState } from 'react';
import { Box, Typography, Button, Stepper, Step, StepLabel, Chip, IconButton } from '@mui/material';
import { ArrowBack, ArrowForward, Close } from '@mui/icons-material';
import { USER_STEPS } from '../../pages/DemoPage';

/**
 * UserDemoFlow
 * --------------------------------------------------------------------------
 * Runner for the User demo steps, reusing the exact step screens from DemoPage.
 *
 * Props:
 *   - stepIndices?: number[]  Subset of USER_STEPS to show (in order). Defaults to all.
 *   - inline?: boolean        When true, renders bare for inline embedding (no header
 *                             chrome). Default false = renders its own header with an X.
 *   - onFinish?: () => void   Called on Finish, Back-from-first-step, or dismiss.
 *
 * Auto-height: the component sizes to its content. When hosted in a Dialog, use
 * scroll="paper" on the Dialog so tall steps scroll within the viewport without a
 * fixed-height box. DemoPage is untouched.
 */

interface UserDemoFlowProps {
  stepIndices?: number[];
  inline?: boolean;
  onFinish?: () => void;
}

const TEAL = '#2D6E6A';

const UserDemoFlow: React.FC<UserDemoFlowProps> = ({ stepIndices, inline = false, onFinish }) => {
  const steps = (stepIndices && stepIndices.length > 0)
    ? stepIndices.map((i) => USER_STEPS[i]).filter(Boolean)
    : USER_STEPS;

  const [step, setStep] = useState(0);
  const isLastStep = step >= steps.length - 1;
  const StepComponent = steps[step]?.component;

  const handleNext = () => {
    if (isLastStep) onFinish?.();
    else setStep((s) => s + 1);
  };
  const handleBack = () => {
    if (step === 0) onFinish?.();
    else setStep((s) => s - 1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header (shown when not inline; inline parent supplies its own header) */}
      {!inline && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2.5, py: 1.5, borderBottom: '1px solid #F0F0F0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 800, color: TEAL, fontFamily: 'Poppins, sans-serif' }}>Why Percoro</Typography>
            <Chip label="A quick tour" size="small" sx={{ bgcolor: '#E8F0EE', color: TEAL, fontWeight: 600 }} />
          </Box>
          <IconButton size="small" aria-label="Close tour" onClick={() => onFinish?.()}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Stepper */}
      <Box sx={{ px: inline ? 0 : 2, pt: 2 }}>
        <Stepper activeStep={step} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
          {steps.map((s, i) => (
            <Step key={i} completed={i < step}>
              <StepLabel sx={{ cursor: 'pointer' }} onClick={() => setStep(i)}>
                {s.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step content (auto-height; no internal scroll box) */}
      <Box sx={{ px: inline ? 0.5 : 2.5, py: 2 }}>{StepComponent && <StepComponent />}</Box>

      {/* Controls */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: inline ? 0.5 : 2.5, py: 2, borderTop: '1px solid #F0F0F0',
          position: 'sticky', bottom: 0, bgcolor: 'white',
        }}
      >
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ textTransform: 'none', color: '#555' }}>
          {step === 0 ? 'Close' : 'Back'}
        </Button>
        <Typography variant="caption" sx={{ color: '#888' }}>
          {step + 1} / {steps.length}
        </Typography>
        <Button
          variant="contained"
          endIcon={isLastStep ? undefined : <ArrowForward />}
          onClick={handleNext}
          sx={{
            textTransform: 'none', bgcolor: TEAL, borderRadius: '24px', px: 3, fontWeight: 600,
            '&:hover': { bgcolor: '#245a57' },
          }}
        >
          {isLastStep ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default UserDemoFlow;
